import { FC, useState } from 'react';
import { get } from 'lodash';
import { Input } from '@packages/utils';
import { Modal } from 'components/Base/Modal';
import { Form, FormItem } from 'components/Base/Form';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import useGetValidationData from 'components/Base/Form/validationRules';
import IconButton from 'components/Library/Buttons/IconButton';
import useForm from 'hooks/useForm';
import { TValidateFieldsError } from 'data/types/generic.types';
import { useUpdateWorkshopTaskNameMutation } from 'redux/apiSlice/workshopTasks.slice';
import { EditPencilOutline } from 'assets/icon/icon';
import {
  TEditCustomTaskFormValues,
  TEditCustomTaskProps,
} from './EditCustomTask.types';

// Row action in the Tasks table Actions column (GROW-4278), rendered only for
// custom tasks. Edit is rename-only: partType has no update endpoint, and Due
// Date / Status are already editable inline on every row.
const EditCustomTask: FC<TEditCustomTaskProps> = ({
  workshopId,
  taskId,
  taskName,
}) => {
  const { form } = useForm<TEditCustomTaskFormValues>();
  const { validationRules } = useGetValidationData();
  const { showToastNotification } = useShowToastNotification();
  const [open, setOpen] = useState(false);
  const [updateTaskName, { isLoading }] = useUpdateWorkshopTaskNameMutation();

  const handleClose = () => {
    form.resetFields();
    setOpen(false);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const name = values.name.trim();
      // Nothing to save; skip the round trip and the success toast.
      if (name === taskName.trim()) {
        handleClose();
        return;
      }
      await updateTaskName({ workshopId, taskId, name }).unwrap();
      showToastNotification({
        message: 'Task has been successfully updated',
        type: ENotificationType.SUCCESS,
      });
      handleClose();
    } catch (error) {
      // Keep the modal open on API errors so the user can retry.
      if (!(error as TValidateFieldsError)?.errorFields) {
        showToastNotification({
          message:
            get(error, 'data.message') ||
            'Could not update the task. Please try again.',
          type: ENotificationType.ERROR,
        });
      }
    }
  };

  return (
    <>
      <IconButton
        icon={EditPencilOutline}
        size={16}
        defaultFill="#656565"
        hoverFill="#333333"
        onClick={() => setOpen(true)}
        ariaLabel="Edit task"
      />
      {open && (
        <Modal
          title="Edit Task"
          open={open}
          onCancel={handleClose}
          onOk={handleSubmit}
          okText="Save"
          cancelText="Cancel"
          confirmLoading={isLoading}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{ name: taskName }}
          >
            <FormItem
              label="Task Title"
              name="name"
              // whitespace: the BE rejects blank names, so catch "   " here.
              rules={[{ ...validationRules.requiredField(), whitespace: true }]}
            >
              <Input
                placeholder="Enter task title"
                maxLength={250}
                autoFocus
                data-cy="edit-custom-task-title-input"
              />
            </FormItem>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default EditCustomTask;
